import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { usePreviewContext } from "../../../contexts/preview-context";
import { useFeatureFlagToggle } from "../../../hooks/useFeatureFlags";

/** Shared state */
const photoPanningState = proxy({
	isPhotoPanning: false,
});

/**
 * Logic to pan a photo
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function usePhotoPanning({
	maxReplaceDelay = 1000,
}: {
	/** When using in a transaction, we can pass Infinity to keep the command replaceable in the undo stack until transaction is closed */
	maxReplaceDelay?: number;
}) {
	const dashboard = useDashboardContext();
	const { getScreenToSpreadTransform } = usePreviewContext();

	const isTransformWithRotateEnabled = useFeatureFlagToggle("transformWithRotate");

	/** An object holding information information for Panning/Zooming a photo in a zone */
	const zonePanCropRef = useRef<eXpress.creator.rendering.ZonePanCrop | undefined>();

	const { isPhotoPanning } = useSnapshot(photoPanningState);

	const start = useCallback(
		function startPhotoPanning({
			selectedZone,
			pointOnScreen,
		}: {
			selectedZone: eXpress.core.PhotoLookZone;
			pointOnScreen: eXpress.math.Point;
		}) {
			photoPanningState.isPhotoPanning = true;

			// Start a new PhotoPanning
			zonePanCropRef.current = dashboard.createZonePanCrop(selectedZone, {
				startPos: pointOnScreen,
				dipToCssScale: eXpress.math.Vector.ones, // didn't use, I think included inside getLocalToDocumentTransform
				getLocalToDocumentTransform: () => {
					const { spread } = selectedZone;
					if (!spread) return eXpress.math.Frame.identity;

					const screenToSpreadTransform = getScreenToSpreadTransform(spread, undefined);
					if (!screenToSpreadTransform) return eXpress.math.Frame.identity;

					return eXpress.math.scale(screenToSpreadTransform.scale);
				},
				maxReplaceDelay,
				isTransformWithRotateEnabled,
			});
		},
		[dashboard, getScreenToSpreadTransform, isTransformWithRotateEnabled, maxReplaceDelay],
	);

	const update = useCallback(function movePhotoPanning(point: eXpress.math.Point) {
		zonePanCropRef.current?.moveTo(point, false);
	}, []);

	const end = useCallback(function endPhotoPanning() {
		if (!zonePanCropRef.current) return;
		zonePanCropRef.current.dispose();
		zonePanCropRef.current = undefined;
		photoPanningState.isPhotoPanning = false;
	}, []);

	const cancel = useCallback(function cancelPhotoPanning() {
		if (!zonePanCropRef.current) return;
		zonePanCropRef.current.cancel();
		zonePanCropRef.current.dispose();
		zonePanCropRef.current = undefined;
		photoPanningState.isPhotoPanning = false;
	}, []);

	return [
		isPhotoPanning,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
